import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { StripeConnectionKind } from "@common/types/stripeConnection";
import { isIncompleteStripeConnection } from "@common/helpers/stripeConnection";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUserIntegrations } from "@hooks/useUserIntegrations";
import { useRefreshStripeExpressCacheMutation } from "@mutations/stripeConnection";

const STRIPE_EXPRESS_PARAM = "stripeExpress";
const STRIPE_EXPRESS_RETURNED = "returned";

const READY_TOAST = "Stripe is ready to collect payments";
const STILL_VERIFYING_TOAST =
    "Stripe is still verifying. You can keep sending invoices.";

/**
 * Handles the redirect back from Stripe-hosted Express KYC. When the URL carries
 * `stripeExpress=returned`, it re-reads the entity's Express connection(s) via a
 * live `refresh-cache` call and toasts the fresh readiness, then strips the
 * query param so a refresh of the page doesn't re-toast.
 */
export function useStripeExpressReturn(): void {
    const [searchParams, setSearchParams] = useSearchParams();
    const { toast } = useToaster();
    const integrations = useUserIntegrations();
    const { mutateAsync: refreshExpressCache } =
        useRefreshStripeExpressCacheMutation();
    const hasProcessedRef = useRef(false);

    // Tied to the component lifetime, not a single effect run. Stripping the
    // `stripeExpress` param below flips `isReturned` and re-runs this effect;
    // React fires the previous run's cleanup on that re-render. Guarding the
    // async toast on an effect-scoped flag would therefore cancel it even though
    // the component is still mounted, so the toast would never fire on the
    // refresh path. This ref only flips false on a real unmount.
    const isMountedRef = useRef(true);
    useEffect(
        () => () => {
            isMountedRef.current = false;
        },
        [],
    );

    const isReturned =
        searchParams.get(STRIPE_EXPRESS_PARAM) === STRIPE_EXPRESS_RETURNED;

    useEffect(() => {
        if (!isReturned || hasProcessedRef.current) {
            return;
        }
        const financialConnections = integrations.data?.financialConnections;
        if (!financialConnections) {
            return;
        }

        hasProcessedRef.current = true;
        setSearchParams(
            (previous) => {
                const next = new URLSearchParams(previous);
                next.delete(STRIPE_EXPRESS_PARAM);
                return next;
            },
            { replace: true },
        );

        const expressConnections = financialConnections
            .map(({ connection }) => connection)
            .filter(
                (connection) =>
                    connection.kind === StripeConnectionKind.EXPRESS,
            );
        if (expressConnections.length === 0) {
            return;
        }

        const incompleteConnections = expressConnections.filter((connection) =>
            isIncompleteStripeConnection(connection),
        );
        if (incompleteConnections.length === 0) {
            toast(READY_TOAST);
            return;
        }

        Promise.all(
            incompleteConnections.map((connection) =>
                refreshExpressCache(connection.id),
            ),
        )
            .then((results) => {
                if (!isMountedRef.current) {
                    return;
                }
                const isReady = results.some((result) => result.chargesEnabled);
                toast(isReady ? READY_TOAST : STILL_VERIFYING_TOAST);
            })
            .catch(() => {
                if (!isMountedRef.current) {
                    return;
                }
                toast(STILL_VERIFYING_TOAST);
            });
    }, [
        isReturned,
        integrations.data,
        setSearchParams,
        toast,
        refreshExpressCache,
    ]);
}
